import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Loader2, Check, X, Wallet } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AdminTitle, Modal, Field } from "@/pages/admin/AdminUI";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

const PAY_STATUS = {
  "Waiting for Payment": "bg-slate-500/15 text-slate-300 border-slate-500/30",
  "Transaction Detected": "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "Confirming": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Paid": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Expired": "bg-red-500/15 text-red-300 border-red-500/30",
  "Failed": "bg-red-500/15 text-red-300 border-red-500/30",
};
const emptyW = { network: "TRC20", address: "", label: "", min_payment: 10, expiry_minutes: 60, min_confirmations: 1, qr_code: "", enabled: true };

export default function AdminPayments() {
  const [wallets, setWallets] = useState(null);
  const [payments, setPayments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyW);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/wallets").then((r) => setWallets(r.data)).catch(() => setWallets([]));
    api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/admin/payment-config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const [cfg, setCfg] = useState({});
  const [cfgForm, setCfgForm] = useState({ trc20_provider: "", erc20_provider: "", bep20_provider: "", trc20_api_key: "", erc20_api_key: "", bep20_api_key: "" });
  const [savingCfg, setSavingCfg] = useState(false);
  const activate = async (w) => { await api.post(`/admin/wallets/${w.id}/activate`); toast.success(`${w.network} set as active`); load(); };
  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const payload = { trc20_provider: cfgForm.trc20_provider, erc20_provider: cfgForm.erc20_provider, bep20_provider: cfgForm.bep20_provider };
      ["trc20_api_key", "erc20_api_key", "bep20_api_key"].forEach((k) => { if (cfgForm[k]) payload[k] = cfgForm[k]; });
      const { data } = await api.put("/admin/payment-config", payload);
      setCfg(data); setCfgForm({ ...cfgForm, trc20_api_key: "", erc20_api_key: "", bep20_api_key: "" });
      toast.success("Provider settings saved");
    } catch (e) { toast.error(apiError(e)); }
    setSavingCfg(false);
  };

  const openNew = () => { setEditing(null); setForm(emptyW); setOpen(true); };
  const openEdit = (w) => { setEditing(w); setForm({ ...emptyW, ...w }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, min_payment: +form.min_payment, expiry_minutes: +form.expiry_minutes, min_confirmations: +form.min_confirmations };
      if (editing) await api.put(`/admin/wallets/${editing.id}`, payload); else await api.post("/admin/wallets", payload);
      toast.success("Wallet saved"); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
    setSaving(false);
  };
  const del = async (w) => { if (!window.confirm("Delete this wallet?")) return; await api.delete(`/admin/wallets/${w.id}`); load(); };
  const confirmPay = async (o) => { try { await api.post(`/admin/payments/${o}/confirm`); toast.success("Payment confirmed"); load(); } catch (e) { toast.error(apiError(e)); } };
  const rejectPay = async (o) => { if (!window.confirm("Reject this payment?")) return; await api.post(`/admin/payments/${o}/reject`); load(); };

  if (wallets === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-400" size={36} /></div>;

  return (
    <div className="space-y-8">
      <AdminTitle title="Payments & Wallets" action={<button onClick={openNew} className="gold-btn text-sm" data-testid="admin-add-wallet"><Plus size={16} /> Add Wallet</button>} />

      <div>
        <h2 className="font-semibold text-white mb-3 flex items-center gap-2"><Wallet size={18} className="text-amber-400" /> USDT Wallets & Networks</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {wallets.map((w) => (
            <div key={w.id} className="card-lux p-5" data-testid={`wallet-card-${w.network}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-white">USDT {w.network}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${w.enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-500/15 text-slate-400"}`}>{w.enabled ? "Enabled" : "Disabled"}</span>
              </div>
              <code className="text-xs text-slate-400 break-all block">{w.address}</code>
              <div className="text-xs text-slate-500 mt-2">Min: {w.min_payment} · Expiry: {w.expiry_minutes}m · Conf: {w.min_confirmations}</div>
              <div className={`text-xs mt-1 ${w.provider_configured ? "text-emerald-400" : "text-amber-400"}`}>{w.provider_configured ? "On-chain verification ready" : "Provider API key not set (manual confirm)"}</div>
              <div className="flex items-center gap-3 mt-3">
                {w.is_active
                  ? <span className="text-xs font-semibold text-emerald-400">● Active receiving wallet</span>
                  : <button onClick={() => activate(w)} className="text-xs text-amber-400 hover:underline" data-testid={`activate-wallet-${w.network}`}>Set as active</button>}
                <button onClick={() => openEdit(w)} className="ml-auto text-slate-400 hover:text-amber-400 text-sm" data-testid={`edit-wallet-${w.network}`}><Pencil size={15} /></button>
                <button onClick={() => del(w)} className="text-slate-400 hover:text-red-400 text-sm"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-semibold text-white mb-3">Blockchain Provider Settings</h2>
        <div className="card-lux p-6 space-y-4" data-testid="provider-config">
          <p className="text-xs text-slate-500">API keys are stored securely on the server and are never shown here. Leave a key blank to keep the current one. Automatic on-chain verification turns on for a network once its key is set.</p>
          {[["TRC20", "trc20"], ["ERC20", "erc20"], ["BEP20", "bep20"]].map(([net, k]) => (
            <div key={k} className="grid sm:grid-cols-12 gap-2 items-center">
              <div className="sm:col-span-2 text-sm font-semibold text-white flex items-center gap-2">
                USDT {net}
                <span className={`h-2 w-2 rounded-full ${cfg[`${k}_configured`] ? "bg-emerald-400" : "bg-slate-600"}`} title={cfg[`${k}_configured`] ? "Configured" : "Not set"} />
              </div>
              <input className="input-lux sm:col-span-5 !py-2 text-xs" placeholder={`Provider URL (${cfg[`${k}_provider`] || "default"})`} value={cfgForm[`${k}_provider`]} onChange={(e) => setCfgForm({ ...cfgForm, [`${k}_provider`]: e.target.value })} data-testid={`cfg-${k}-provider`} />
              <input type="password" className="input-lux sm:col-span-5 !py-2 text-xs" placeholder={cfg[`${k}_configured`] ? "•••••• (key set — enter to replace)" : "API key"} value={cfgForm[`${k}_api_key`]} onChange={(e) => setCfgForm({ ...cfgForm, [`${k}_api_key`]: e.target.value })} data-testid={`cfg-${k}-key`} />
            </div>
          ))}
          <button onClick={saveCfg} disabled={savingCfg} className="gold-btn text-sm" data-testid="save-provider-config">{savingCfg ? <Loader2 className="animate-spin" size={16} /> : "Save Provider Settings"}</button>
        </div>
      </div>

      <div>
        <h2 className="font-semibold text-white mb-3">Payment Records</h2>
        <div className="card-lux overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-payments-table">
              <thead className="bg-black/30 text-slate-500 text-xs uppercase"><tr><th className="text-left px-5 py-3">Order</th><th className="text-left px-5 py-3">Amount</th><th className="text-left px-5 py-3">Network</th><th className="text-left px-5 py-3">Tx Hash</th><th className="text-left px-5 py-3">Status</th><th className="text-right px-5 py-3">Actions</th></tr></thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-white/5" data-testid={`payment-row-${p.order_id}`}>
                    <td className="px-5 py-3 font-mono text-xs text-slate-400">{p.order_id}</td>
                    <td className="px-5 py-3 text-white">{p.amount} USDT</td>
                    <td className="px-5 py-3 text-slate-400">{p.network}</td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-500 max-w-[160px] truncate">{p.tx_hash || "—"}</td>
                    <td className="px-5 py-3"><StatusBadge status={p.status} map={PAY_STATUS} /></td>
                    <td className="px-5 py-3 text-right space-x-2">
                      {p.status !== "Paid" && <button onClick={() => confirmPay(p.order_id)} className="text-emerald-400 hover:text-emerald-300" title="Confirm" data-testid={`confirm-payment-${p.order_id}`}><Check size={16} /></button>}
                      {p.status !== "Paid" && p.status !== "Failed" && <button onClick={() => rejectPay(p.order_id)} className="text-red-400 hover:text-red-300" title="Reject"><X size={16} /></button>}
                    </td>
                  </tr>
                ))}
                {payments.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No payments yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Wallet" : "Add Wallet"}>
        <form onSubmit={save} className="space-y-4" data-testid="wallet-form">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Network">
              <select className="input-lux" value={form.network} onChange={(e) => setForm({ ...form, network: e.target.value })} data-testid="wallet-network-field">
                <option value="TRC20">USDT TRC20</option><option value="ERC20">USDT ERC20</option><option value="BEP20">USDT BEP20</option>
              </select>
            </Field>
            <Field label="Label"><input className="input-lux" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></Field>
          </div>
          <Field label="Wallet Address"><input required className="input-lux font-mono text-xs" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="wallet-address-field" /></Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Min Payment"><input type="number" className="input-lux" value={form.min_payment} onChange={(e) => setForm({ ...form, min_payment: e.target.value })} /></Field>
            <Field label="Expiry (min)"><input type="number" className="input-lux" value={form.expiry_minutes} onChange={(e) => setForm({ ...form, expiry_minutes: e.target.value })} /></Field>
            <Field label="Min Confirms"><input type="number" className="input-lux" value={form.min_confirmations} onChange={(e) => setForm({ ...form, min_confirmations: e.target.value })} /></Field>
          </div>
          <Field label="QR Image URL (optional)"><input className="input-lux" value={form.qr_code} onChange={(e) => setForm({ ...form, qr_code: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="accent-amber-500" data-testid="wallet-enabled" /> Enabled</label>
          <button disabled={saving} className="gold-btn w-full" data-testid="wallet-save-btn">{saving ? <Loader2 className="animate-spin" size={18} /> : "Save Wallet"}</button>
        </form>
      </Modal>
    </div>
  );
}
